import React, { useState, useEffect, useRef } from 'react';
import './DesignComponent.css';
import { LayoutGetbyIdhooks } from '../../Helper/LayoutHelper';
import { ResolutionListhooks } from '../../Helper/ResolutionHelper';
import { FILE_BASE_URL } from '../../Config/constants';
import ClockRenderer from './DemoModules/ClockRenderer';
import RSSRenderer from './DemoModules/RSSRenderer';

function getYouTubeEmbedUrl(originalUrl, autoplay = true) {
  const match = originalUrl.match(/[?&]v=([^&]+)/) || originalUrl.match(/youtu\.be\/([^?&]+)/);
  const videoId = match ? match[1] : '';
  if (!videoId) return '';

  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    mute: '1', // 👈 important for autoplay to work
    controls: '1',
    modestbranding: '1',
    rel: '0',
  });

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

function parseLayoutXML(xmlString) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const layoutElement = xmlDoc.querySelector('layout');
  const layout = {
    width: parseInt(layoutElement.getAttribute('width')),
    height: parseInt(layoutElement.getAttribute('height')),
    duration: parseInt(layoutElement.getAttribute('dur')),
  };

  const regionElements = xmlDoc.querySelectorAll('region');
  const regions = Array.from(regionElements).map(regionElement => {
    const region = {
      class: regionElement.getAttribute('class'),
      width: parseInt(regionElement.getAttribute('width')),
      height: parseInt(regionElement.getAttribute('height')),
      top: parseInt(regionElement.getAttribute('top')),
      left: parseInt(regionElement.getAttribute('left')),
      color: regionElement.getAttribute('color'),
      repeat: regionElement.getAttribute('repeat') === 'true',
      media: [],
    };

    const mediaElements = regionElement.querySelectorAll('media');
    region.media = Array.from(mediaElements).map(mediaElement => {
      const type = mediaElement.getAttribute('type');
      const base = {
        type,
        name: mediaElement.getAttribute('name'),
        title: mediaElement.getAttribute('title'),
        duration: parseInt(mediaElement.getAttribute('dur')),
        size: parseInt(mediaElement.getAttribute('size')),
        color: mediaElement.getAttribute('color'),
        behave: mediaElement.getAttribute('behave'),
        text: mediaElement.getAttribute('text'),
        id: mediaElement.getAttribute('id'),
        maxItems: mediaElement.getAttribute('maxItems'),
      };

      if (type === 'Clock') {
        return {
          ...base,
          style: mediaElement.getAttribute('style') || 'Digital',
          format: mediaElement.getAttribute('format') || 'HH:mm:ss',
          offsetMinutes: parseInt(mediaElement.getAttribute('offsetMinutes')) || 0,
          timeZone: mediaElement.getAttribute('timeZone') || 'Local',
          showSeconds: mediaElement.getAttribute('showSeconds') === 'true'
        };
      }

      if (type === 'YouTube') {
        return {
          ...base,
          url: mediaElement.getAttribute('url'),
          autoplay: mediaElement.getAttribute('autoplay') || 'false',
          fullscreen: parseInt(mediaElement.getAttribute('fullscreen')) || 'false',
        };
      }

      return base;
    });

    return region;
  });

  return { layout, regions };
}

function DemoLayoutComponent(props) {
  const [resolution, setResolution] = useState('');
  const [parsedLayout, setParsedLayout] = useState(null);
  const [layoutData, setLayoutData] = useState({
    layoutId: props.id,
    name: '',
    xmlDesign: '',
    createdBy: null,
  });

  const [, forceUpdate] = useState(0);
  const currentMediaIndicesRef = useRef([]);
  const mediaTimersRef = useRef([]);
  const requestRef = useRef();
  const previousTimeRef = useRef();
  const videoRefs = useRef({});

  useEffect(() => {
    GetResolution();
  }, []);

  async function GetResolution() {
    try {
      const data = await ResolutionListhooks();
      if (Array.isArray(data.items)) {
        getData(data.items);
      } else {
        console.error('Resolution data is not an array:', data);
      }
    } catch (err) {
      console.error('Error fetching resolutions:', err);
    }
  }

  async function getData(resolutions) {
    const data = await LayoutGetbyIdhooks(props.id);
    if (data !== undefined) {
      const parsedData = parseLayoutXML(data.xmlDesign);
      const currentResolution = resolutions.find(x => x.resolutionId === data.resolutionId);

      currentMediaIndicesRef.current = parsedData.regions.map(() => 0);
      mediaTimersRef.current = parsedData.regions.map(region =>
        region.media.length > 0 ? region.media[0].duration * 1000 : 0
      );

      setResolution(currentResolution);
      setParsedLayout(parsedData);
      setLayoutData(data);
    }
  }

  useEffect(() => {
    if (parsedLayout) {
      const loopMedia = (timestamp) => {
        if (previousTimeRef.current === undefined) {
          previousTimeRef.current = timestamp;
        }

        const deltaTime = timestamp - previousTimeRef.current;
        previousTimeRef.current = timestamp;

        let mediaChanged = false;

        parsedLayout.regions.forEach((region, regionIndex) => {
          if (region.media.length === 0) return;

          const currentIndex = currentMediaIndicesRef.current[regionIndex];
          const currentMedia = region.media[currentIndex];

          // ✅ Handle Text, Video, and Image transitions
          if (currentMedia.duration === 0) return; // support for infinite duration

          mediaTimersRef.current[regionIndex] -= deltaTime;

          if (mediaTimersRef.current[regionIndex] <= 0) {
            const nextIndex = (currentIndex + 1) % region.media.length;
            currentMediaIndicesRef.current[regionIndex] = nextIndex;
            mediaTimersRef.current[regionIndex] = region.media[nextIndex].duration * 1000;
            mediaChanged = true;
          }
        });

        if (mediaChanged) {
          forceUpdate(u => u + 1);
        }

        requestRef.current = requestAnimationFrame(loopMedia);
      };

      requestRef.current = requestAnimationFrame(loopMedia);

      return () => {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
      };
    }
  }, [parsedLayout]);

  const generateRandomString = (length) => {
    const characters = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let randomString = '';
    for (let i = 0; i < length; i++) {
      randomString += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return randomString;
  };

  if (!parsedLayout) {
    return <div>Loading...</div>;
  }

  const { layout, regions } = parsedLayout;

  return (
    <div className="container-fluid content-top-gap" style={{ minHeight: '600px' }}>
      <div className='container mb-2'>
        <div className="text-center">
          <h4>{props.name !== null ? ' Layout Name - ' + props.name : ''}</h4>
          <p><b>Layout Details - </b>Aspect Ratio: 16:9 | Background Color: Black | Resolution Type:
            {resolution?.resolutionType}
          </p>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="container">
            <div
              id="layout"
              layout_scale={resolution?.scale}
              main_width={resolution?.mainWidth}
              main_height={resolution?.mainHeight}
              style={{
                marginLeft: '1%',
                position: 'relative',
                width: resolution?.intentWidth + 'px',
                height: resolution?.intentHeight + 'px',
                backgroundColor: '#000000',
              }}
            >
              {regions.map((region, regionIndex) => {
                const regionid = generateRandomString(6);

                const regionwid = Math.round(region.width * resolution.scale);
                const regionhei = Math.round(region.height * resolution.scale);
                const regiontop = Math.round(region.top * resolution.scale);
                const regionleft = Math.round(region.left * resolution.scale);

                const currentMediaIndex = currentMediaIndicesRef.current[regionIndex];
                const currentMedia = region.media[currentMediaIndex];

                return (
                  <div
                    key={regionid}
                    style={{
                      position: 'absolute',
                      width: regionwid,
                      height: regionhei,
                      top: regiontop,
                      left: regionleft,
                    }}
                  >
                    <div className="region" style={{ width: '100%', height: '100%' }}>
                      <div className="media-display" style={{ width: '100%', height: '100%' }}>
                        {currentMedia.type === 'Video' && (
                          <div style={{ width: '100%', height: '100%', backgroundColor: 'black' }}>
                            <video
                              key={currentMedia.name}
                              ref={(el) => {
                                if (el) {
                                  videoRefs.current[regionIndex] = el;
                                  el.oncanplay = () => {
                                    el.classList.add('ready');
                                  };
                                  el.classList.remove('ready');
                                }
                              }}
                              src={`${FILE_BASE_URL}/${encodeURIComponent(currentMedia.name)}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'fill',
                              }}
                              autoPlay
                              loop
                              muted
                              playsInline
                              onError={() => console.error('Video failed to load:', currentMedia.name)}
                            />
                          </div>
                        )}
                        {currentMedia.type === 'Image' && (
                          <img
                            src={`${FILE_BASE_URL}/${encodeURIComponent(currentMedia.name)}`}
                            alt=""
                            style={{ width: '100%', height: '100%' }}
                          />
                        )}
                        {currentMedia.type === 'Text' && (
                          currentMedia.behave?.toLowerCase().includes('scroll') ? (
                            <div
                              className="scroll-wrapper"
                            >
                              <div
                                className="scroll-text-inner"
                                style={{
                                  color: currentMedia.color,
                                  fontSize: `${currentMedia.size}px`,
                                  animation:
                                    currentMedia.behave === 'Left Scroll'
                                      ? `scroll-left ${currentMedia.duration}s linear infinite`
                                      : currentMedia.behave === 'Right Scroll'
                                        ? `scroll-right ${currentMedia.duration}s linear infinite`
                                        : currentMedia.behave === 'Up Scroll'
                                          ? `scroll-up ${currentMedia.duration}s linear infinite`
                                          : currentMedia.behave === 'Down Scroll'
                                            ? `scroll-down ${currentMedia.duration}s linear infinite`
                                            : 'none',
                                }}
                              >
                                {currentMedia.text}
                              </div>
                            </div>
                          ) : (
                            <div
                              style={{
                                color: currentMedia.color,
                                fontSize: `${currentMedia.size}px`,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {currentMedia.text}
                            </div>
                          )
                        )}
                        {currentMedia.type === 'Website' && (
                          <iframe
                            src={currentMedia.text}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title={`Website-${regionIndex}`}
                            sandbox="allow-scripts allow-same-origin allow-forms"
                          />
                        )}
                        {currentMedia.type === 'Clock' && (
                          <ClockRenderer
                            key={`${currentMedia.id}-${currentMedia.style}-${Math.random()}`} // Force re-render on every entry
                            styleType={currentMedia.style}
                            format={currentMedia.format}
                            offsetMinutes={parseInt(currentMedia.offsetMinutes || '0')}
                            timeZone={currentMedia.timeZone || 'Local'}
                            showSeconds={currentMedia.showSeconds === 'true' || currentMedia.showSeconds === true}
                          />
                        )}
                        {currentMedia.type === 'YouTube' && currentMedia.url && (
                          <iframe
                            width="100%"
                            height="100%"
                            src={getYouTubeEmbedUrl(currentMedia.url, currentMedia.autoplay)}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen={currentMedia.fullscreen}
                            title={currentMedia.title || 'YouTube Video'}
                            style={{ border: 'none' }}
                          />
                        )}
                        {currentMedia.type === 'RssFeed' && (
                          <RSSRenderer
                            feedUrl={currentMedia.text}
                            maxItems={currentMedia.maxItems || 5}
                            durationPerItem={currentMedia.duration || 5}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoLayoutComponent;
